import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, CircularProgress } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { parseISO } from 'date-fns';
import { MessageSquare } from 'lucide-react';

import { LISTING_ENQUIRIES_OVER_TIME } from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

import {
  CHART_LEGEND_SLOT_PROPS,
  CHART_TOOLTIP_SX,
  ChartEmptyState,
} from './ChartControls';
import {
  type Granularity,
  SERIES_COLORS,
  aggregateStacked,
  buildBuckets,
} from './chartUtils';

// Embeddable enquiries-by-portal chart, rendered inside the funnel's Enquiries
// drill-down.
export const ListingEnquiriesBody = ({
  lotId,
  start,
  end,
  granularity,
}: {
  lotId: string;
  start: Date;
  end: Date;
  granularity: Granularity;
}) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(LISTING_ENQUIRIES_OVER_TIME, {
    variables: { lotId },
  });

  const chart = useMemo(() => {
    const enquiries = data?.enquiries ?? [];
    const buckets = buildBuckets(start, end, granularity);
    const series = aggregateStacked(
      enquiries,
      enquiry => parseISO(enquiry.created_at),
      enquiry => enquiry.portal?.name ?? t('Direct'),
      buckets,
    );
    return { labels: buckets.labels, series };
  }, [data, start, end, granularity, t]);

  const hasEnquiries = chart.series.some(series =>
    series.data.some(value => value > 0),
  );

  if (loading) {
    return (
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error != null) {
    return (
      <ChartEmptyState
        icon={<MessageSquare size={22} />}
        message={t('Failed to load data')}
      />
    );
  }

  if (!hasEnquiries) {
    return (
      <ChartEmptyState
        icon={<MessageSquare size={22} />}
        message={t('No enquiries in this period')}
      />
    );
  }

  return (
    <BarChart
      height={260}
      xAxis={[
        {
          data: chart.labels,
          scaleType: 'band',
          tickLabelStyle: { fontSize: 10 },
        },
      ]}
      series={chart.series.map((item, index) => ({
        data: item.data,
        label: item.label,
        stack: 'total',
        color: SERIES_COLORS[index % SERIES_COLORS.length],
      }))}
      margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
      slotProps={{
        tooltip: { sx: CHART_TOOLTIP_SX },
        legend: CHART_LEGEND_SLOT_PROPS,
      }}
    />
  );
};
